import { sha256 } from "js-sha256";

import {
  CancelOrderVariables,
  ConfirmReturnVariables,
  CreateShipmentVariables,
  CreateWithdrawalResponse,
  CreateWithdrawalVariables,
  FetchOrderRegisteredItemsVariables,
  FetchReturnMethodsNotesVariables,
  FetchOrderVariables,
  FetchProductDetailsVariables,
  FetchReturnMethodsVariables,
  CreatePayPalOrderVariables,
  ConfirmReturnPaymentVariables,
} from "~/common/types/api";
import {
  AuthorizePayPalOrderResponse,
  CreatePayPalOrderResponse,
} from "~/common/types/paypal";
import { ProductRaw } from "~/common/types/product";
import {
  CollectionDetailsRaw,
  CollectionRaw,
  ProductSearchResponseRaw,
} from "~/common/types/collection";
import { ConfirmReturnResponseRaw } from "~/hooks/useConfirmReturn/useConfirmReturn.types";
import { CookiesSettingsRaw } from "~/hooks/useCookiesSettings/useCookiesSettings.types";
import { CreateShipmentResponseRaw } from "~/hooks/useCreateShipment/useCreateShipment.types";
import { DHLDownStatusResponseRaw } from "~/hooks/useDHLDownStatus/useDHLDownStatus.types";
import { MerchantSettingsResponseRaw } from "~/hooks/useMerchantSettings/useMerchantSettings.types";
import { OrderDetailsResponseRaw } from "~/hooks/useOrderDetails/useOrderDetails.types";
import { OrderRegisteredItemsResponseRaw } from "~/hooks/useOrderRegisteredItems/useOrderRegisteredItems.types";
import { ReturnMethodsResponseRaw } from "~/hooks/useReturnMethods/useReturnMethods.types";
import { ReturnMethodsNotesResponseRaw } from "~/hooks/useReturnMethodsNotes/useReturnMethodsNotes.types";
import { TradeInItemsResponseRaw } from "~/hooks/useTradeInItems/useTradeInItems.types";
import {
  getConfirmReturnPaymentVariablesFromPaymentAuthorizationResponse,
  getConfirmReturnRequestPayload,
  getCreatePayPalOrderRequestPayload,
  getCreateShipmentRequestPayload,
  getFetchOrderQueryString,
  getFetchOrderRegisteredItemsQueryString,
  getFetchProductDetailsQueryString,
  getFetchReturnMethodsNotesQueryString,
  getFetchReturnMethodsRequestPayload,
} from "~/util/api";
import { getXCSRFToken } from "~/util/getXCSRFToken";
import { logoutOnTemporaryRedirect } from "~/util/importWithRetry";
import { portalPreviewHeaders } from "~/util/previewSession";

export const fetchMerchantSettings = () => {
  return fetch("/settings", {
    headers: portalPreviewHeaders(),
  }).then<MerchantSettingsResponseRaw>((response) => response.json());
};

export const fetchCookiesSettings = () => {
  return fetch("/settings/cookie_enabled").then<CookiesSettingsRaw>(
    (response) => {
      return response.json();
    }
  );
};

// The backend's captcha tier answered the lookup with
// 403 {"error": "captcha_required", "challenge_url": ...}. Carrying the
// challenge URL lets the FE render the ALTCHA widget and retry with a solved
// payload instead of surfacing the error as a failed lookup.
export class CaptchaRequiredError extends Error {
  readonly challengeUrl: string;

  constructor(challengeUrl: string) {
    super("captcha_required");
    this.name = "CaptchaRequiredError";
    this.challengeUrl = challengeUrl;
  }
}

export const fetchOrder = (params: FetchOrderVariables) => {
  const queryString = getFetchOrderQueryString(params);
  // The solved payload rides as a top-level param, outside the filter[]
  // builder — the backend reads params[:altcha].
  const altchaParam = params.altcha
    ? `&altcha=${encodeURIComponent(params.altcha)}`
    : "";
  return fetch(`/orders.json?${queryString}${altchaParam}`)
    .then<OrderDetailsResponseRaw>((response) => response.json())
    .then((response) => {
      if (response.error === "captcha_required") {
        throw new CaptchaRequiredError(response.challenge_url);
      }
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    });
};

export const fetchOrderRegisteredItems = (
  params: FetchOrderRegisteredItemsVariables
) => {
  const queryString = getFetchOrderRegisteredItemsQueryString(params);
  return fetch(
    `/orders/registered_items.json?${queryString}`
  ).then<OrderRegisteredItemsResponseRaw>((response) => response.json());
};

export const fetchReturnMethods = async (
  params: FetchReturnMethodsVariables
) => {
  const requestPayload = getFetchReturnMethodsRequestPayload(params);
  const response = await fetch(`/rules`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getXCSRFToken(),
    },
    body: JSON.stringify(requestPayload),
  });
  logoutOnTemporaryRedirect({ response });
  const data: ReturnMethodsResponseRaw = await response.json();
  return data;
};

export const createShipment = async (params: CreateShipmentVariables) => {
  const requestPayload = getCreateShipmentRequestPayload(params);
  const response = await fetch("/shipments", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getXCSRFToken(),
    },
    body: JSON.stringify(requestPayload),
  });
  if (response.status === 500) {
    throw new Error(
      "Failed to create return label. Please recheck your address and try again or contact customer support"
    );
  }
  const data: CreateShipmentResponseRaw = await response.json();
  if (data.error?.length) {
    throw new Error(data.error[0]);
  }
  return data;
};

export const confirmReturn = async (params: ConfirmReturnVariables) => {
  const requestPayload = getConfirmReturnRequestPayload(params);

  const checksum = sha256.hmac(getXCSRFToken(), JSON.stringify(requestPayload));
  const response = await fetch("/refunds", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getXCSRFToken(),
      "X-Checksum": checksum,
    },
    body: JSON.stringify(requestPayload),
  });
  logoutOnTemporaryRedirect({ response, skipToast: true }); // skip showing same toast twice on the page
  if (response.status >= 500) {
    throw new Error(
      "Failed to confirm return. Please try again or contact customer support"
    );
  }
  const data: ConfirmReturnResponseRaw = await response.json();
  if (data.error) {
    throw new Error(data.error);
  }
  return data;
};

// Cancels an unfulfilled Shopify order via POST /orders/cancel. The lookup
// filter (order-number + email) is the credential and the request is
// CSRF-protected — there is intentionally no payload checksum (the server-side
// fresh re-lookup + eligibility re-check is the real guard).
export const cancelOrder = async ({
  orderId,
  customerEmail,
}: CancelOrderVariables) => {
  const requestPayload = {
    filter: {
      invoice_number: orderId.trim(),
      customer: { email: customerEmail.trim() },
    },
  };

  const response = await fetch("/orders/cancel", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getXCSRFToken(),
    },
    body: JSON.stringify(requestPayload),
  });
  logoutOnTemporaryRedirect({ response, skipToast: true });

  const data = await response.json();
  if (!response.ok || data.error) {
    throw new Error(
      data.error ||
        "Failed to cancel order. Please try again or contact customer support"
    );
  }
  return data;
};

// Submits a withdrawal declaration via POST /withdrawals. As with cancelOrder,
// the lookup filter (order-number + email) is the credential and the request is
// CSRF-protected; the server performs a fresh authoritative re-lookup and
// re-checks the window/duplicate gates, so nothing in the body is trusted.
export const createWithdrawal = async ({
  filter,
  lineItems,
  locale,
  cancelOrder = false,
}: CreateWithdrawalVariables): Promise<CreateWithdrawalResponse> => {
  const requestPayload = {
    filter,
    line_items: lineItems,
    locale,
    cancel_order: cancelOrder,
  };

  const response = await fetch("/withdrawals", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getXCSRFToken(),
    },
    body: JSON.stringify(requestPayload),
  });
  logoutOnTemporaryRedirect({ response, skipToast: true });

  const data = await response.json();
  if (!response.ok || data.error) {
    throw new Error(
      data.error ||
        "Failed to declare withdrawal. Please try again or contact customer support"
    );
  }
  return data;
};

export const fetchReturnMethodsNotes = async (
  params: FetchReturnMethodsNotesVariables
) => {
  const queryString = getFetchReturnMethodsNotesQueryString(params);
  const response = await fetch(
    `/merchant_return_methods/${params.merchantReturnMethodId}/notes?${queryString}`
  );
  const result: ReturnMethodsNotesResponseRaw = await response.json();
  return result;
};

export const fetchProductDetails = async (
  {
    productId,
    orderId,
    exchangedOrderItemId,
    useShopProductEndpoint,
  }: FetchProductDetailsVariables,
  countryCode: string
) => {
  if (useShopProductEndpoint) {
    const response = await fetch(
      `/shop_now/products/${productId}.json?pricing_country=${countryCode}`
    );
    logoutOnTemporaryRedirect({ response });
    const data: ProductRaw = await response.json();
    return data;
  }
  const queryString = getFetchProductDetailsQueryString({
    orderId,
    exchangedOrderItemId,
  });
  return fetch(`/products/${productId}${queryString}`).then<ProductRaw>(
    (response) => response.json()
  );
};

export const fetchDHLDownStatus = () =>
  fetch("/settings/dhl_not_working").then<DHLDownStatusResponseRaw>(
    (response) => response.json()
  );

export const fetchTradeInItems = (orderId: string) =>
  fetch(`/trade_in_items?order_id=${orderId}`).then<TradeInItemsResponseRaw>(
    (response) => response.json()
  );

export const createPayPalOrder = async (params: CreatePayPalOrderVariables) => {
  const requestPayload = getCreatePayPalOrderRequestPayload(params);
  const response = await fetch("/payment/orders", {
    method: "POST",
    body: JSON.stringify(requestPayload),
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getXCSRFToken(),
    },
  });
  logoutOnTemporaryRedirect({ response });
  const order: CreatePayPalOrderResponse = await response.json();
  return order.id;
};

export const authorizePayPalOrder = (orderId: string) =>
  fetch(`/payment/orders/${orderId}/authorize`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getXCSRFToken(),
    },
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((errorData) => {
          throw new Error(errorData?.error || "Payment authorization failed");
        });
      }
      return response.json() as Promise<AuthorizePayPalOrderResponse>;
    })
    .then<ConfirmReturnPaymentVariables>((data) =>
      getConfirmReturnPaymentVariablesFromPaymentAuthorizationResponse(data)
    );

export const fetchCollections = async (countryCode) => {
  const response = await fetch(
    `/shop_now/collections?product_limit=4&pricing_country=${countryCode}`
  );
  logoutOnTemporaryRedirect({ response });
  const data: CollectionRaw[] = await response.json();
  return data;
};

export const fetchCollection = async (
  collectionId: string,
  countryCode: string,
  nextPage?: string,
  prevPage?: string
) => {
  let queryParam = "";
  if (nextPage) {
    queryParam = `next_page=${nextPage}`;
  }
  if (prevPage) {
    queryParam = `prev_page=${prevPage}`;
  }
  const response = await fetch(
    `/shop_now/collections/${collectionId}?${queryParam}&pricing_country=${countryCode}&product_limit=12`
  );
  logoutOnTemporaryRedirect({ response });
  const data: CollectionDetailsRaw = await response.json();
  return data;
};

export const fetchSearchProducts = async (
  searchQuery: string,
  countryCode: string,
  nextPage?: string,
  prevPage?: string
) => {
  let paginationQueryParam = "";
  if (nextPage) {
    paginationQueryParam = `next_page=${nextPage}`;
  }
  if (prevPage) {
    paginationQueryParam = `prev_page=${prevPage}`;
  }
  const response = await fetch(
    `/shop_now/search_products?query=${searchQuery}&pricing_country=${countryCode}&product_limit=12&${paginationQueryParam}`
  );
  logoutOnTemporaryRedirect({ response });
  const data: ProductSearchResponseRaw = await response.json();
  return data;
};
